import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { FolderOpen, Search } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { api } from "../../lib/api";
import { NewCollectionDialog } from "./NewCollectionDialog";

export function CollectionsList() {
  const search = useSearch({ strict: false }) as { tag?: string };
  const [q, setQ] = useState("");
  const collections = useQuery({
    queryKey: ["collections", { tag: search.tag, q }],
    queryFn: () => api.listCollections({ tag: search.tag, q: q || undefined }),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <header className="mb-6 flex items-baseline justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">Colecciones</h1>
          <p className="text-sm text-(--text-tertiary)">
            Agrupaciones planas de documentos, apuntes y assets. Las etiquetas las atraviesan.
          </p>
        </div>
        <NewCollectionDialog />
      </header>

      <div className="mb-4 flex items-center gap-2">
        {search.tag && (
          <Badge variant="default" className="text-[10px]">
            tag: #{search.tag}
          </Badge>
        )}
        <div className="relative max-w-md flex-1">
          <Search
            size={13}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-(--text-tertiary)"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar colecciones…"
            className="h-9 pl-7"
            data-testid="collections-q"
          />
        </div>
      </div>

      {collections.isLoading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {collections.data?.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center text-(--text-tertiary)">
          {search.tag
            ? `Sin colecciones para la etiqueta #${search.tag}`
            : "Aún no hay colecciones. Crea una para empezar."}
        </div>
      )}

      <ul
        data-testid="collections-grid"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        {collections.data?.items.map((col) => (
          <li
            key={col.id}
            className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4 hover:bg-(--bg-hover)"
          >
            <Link
              to="/collections/$slug"
              params={{ slug: col.slug }}
              className="block no-underline"
            >
              <div className="flex items-center gap-2">
                <FolderOpen size={14} className="text-(--accent-500)" />
                <h3 className="text-base font-semibold">{col.title}</h3>
              </div>
              {col.description && (
                <p className="mt-1 line-clamp-2 text-xs text-(--text-secondary)">
                  {col.description}
                </p>
              )}
              <footer className="mt-3 flex items-center gap-1.5 text-xs text-(--text-tertiary)">
                <Badge variant="muted" className="text-[10px]">
                  {col.item_count} items
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {col.visibility}
                </Badge>
              </footer>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
